import React, { FC, useContext } from "react";
import { GridLegacy as Grid } from "@mui/material";

import { IBookingScanOptions, IPreferences } from "../../logic/api";
import { isSysAdmin } from "../../utility/utils";
import EditableField, { EditableFieldTypes } from "../../components/editable/EditableField/EditableField";
import EditableGroup, { EditableGroupType } from "../../components/editable/EditableGroup/EditableGroup";
import NotchedCard from "../../components/cards/NotchedCard/NotchedCard";
import { SessionContext } from "../../utility/useSession";
import styles from "./styles";
import { useTranslation } from "react-i18next";
import { ReleaseFlag, useReleaseManagement } from "@/utility/feature-management";
import { usePreferences } from "@/utility/usePreferences";
interface BookingScanOptionSettingsProps {
    onSave: (data: Partial<IPreferences>) => Promise<boolean>;
}

export const BookingScanOptionSettings: FC<BookingScanOptionSettingsProps> = ({ onSave }) => {
    const { useTrackingId } = usePreferences();
    const session = useContext(SessionContext);
    const { t } = useTranslation();
    const isScanByExternalReferenceReleased = useReleaseManagement(ReleaseFlag.releaseAdditionalExternalReferences);

    if (!session.current.site) {
        return null;
    }

    if (!isSysAdmin(session)) {
        return null;
    }


    const ensureScanOptions = (data: IPreferences): IBookingScanOptions => {
        if (!data.bookingScanOptions?.properties) {
            data.bookingScanOptions = {
                properties: {
                    enableBookingId: true,
                    enableRef: true,
                    enableImportedId: true,
                    enableUuid: true,
                    enableExternalReferences: isScanByExternalReferenceReleased ? true : false
                }
            };
        }
        return data.bookingScanOptions;
    };

    return (
        <Grid item xs={12}>
            <NotchedCard label={t("settings.scanOptions")} style={styles.card}>
                <EditableGroup<IPreferences>
                    id="bookingScanOptions"
                    editType={EditableGroupType.switchableEditing}
                    data={session.current.site.preferences}
                    onDataChanged={(data) => onSave({
                        bookingScanOptions: data.bookingScanOptions
                    })}
                    testId="booking-scan-options"
                >
                    <Grid container>
                        <Grid item sm={8} xs={12}></Grid>
                        <Grid item sm={2} xs={6}>
                            <EditableField<IPreferences, boolean>
                                useFullWidthWhenEditing={false}
                                inputType={EditableFieldTypes.switch}
                                label={t("settings.scanOptionsUuid")}
                                onProvide={(data) => Boolean(data.bookingScanOptions?.properties.enableUuid ?? true)}
                                onChange={(data, value) => ensureScanOptions(data).properties.enableUuid = value}
                                testId="settings-scan-options-uuid-switch"
                            />
                            <EditableField<IPreferences, boolean>
                                useFullWidthWhenEditing={false}
                                inputType={EditableFieldTypes.switch}
                                label={t("settings.scanOptionsRef")}
                                onProvide={(data) => Boolean(data.bookingScanOptions?.properties.enableRef ?? true)}
                                onChange={(data, value) => ensureScanOptions(data).properties.enableRef = value}
                                testId="settings-scan-options-ref-switch"
                            />

                        </Grid>
                        <Grid item sm={2} xs={6}>

                            <EditableField<IPreferences, boolean>
                                useFullWidthWhenEditing={false}
                                inputType={EditableFieldTypes.switch}
                                label={t("settings.scanOptionsBookingId")}
                                onProvide={(data) => Boolean(data.bookingScanOptions?.properties.enableBookingId ?? true)}
                                onChange={(data, value) => ensureScanOptions(data).properties.enableBookingId = value}
                                testId="settings-scan-options-booking-id-switch"
                            />
                            <EditableField<IPreferences, boolean>
                                useFullWidthWhenEditing={false}
                                inputType={EditableFieldTypes.switch}
                                label={t("settings.scanOptionsImportedId")}
                                onProvide={(data) => Boolean(data.bookingScanOptions?.properties.enableImportedId ?? true)}
                                onChange={(data, value) => ensureScanOptions(data).properties.enableImportedId = value}
                                testId="settings-scan-options-imported-id-switch"
                            />
                            {isScanByExternalReferenceReleased && useTrackingId &&
                                <EditableField<IPreferences, boolean>
                                    useFullWidthWhenEditing={false}
                                    inputType={EditableFieldTypes.switch}
                                    label={t("settings.scanOptionsExternalReferences")}
                                    onProvide={(data) => Boolean(data.bookingScanOptions?.properties.enableExternalReferences ?? true)}
                                    onChange={(data, value) => ensureScanOptions(data).properties.enableExternalReferences = value}
                                    testId="settings-scan-options-external-references-switch"
                                />
                            }
                        </Grid>
                    </Grid>
                </EditableGroup>
            </NotchedCard>
        </Grid>
    );
};